import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Linkify from "linkify-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UiNotification } from "@/generated/ui-api";
import { getNotifications, markNotificationAsRead } from "@/generated/ui-api";
import {
  Button,
  ErrorDisplay,
  FormInput,
  HeadlessTable,
  Loader,
  Modal,
  PageTitle,
} from "@/shared/components";
import { useSearchParams } from "react-router-dom";
import {
  type ColumnDef,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { useBreadcrumb } from "@/shared/hooks";

export default function MessagesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setBreadcrumbs } = useBreadcrumb();

  const page = Number(searchParams.get("page")) || 1;
  const unreadOnly = searchParams.get("unreadOnly") === "true";
  const keyword = searchParams.get("keyword") || "";
  const [keywordInput, setKeywordInput] = useState(keyword);

  const pageSize = 20;

  useEffect(() => {
    setBreadcrumbs([{ label: t("messagesPage.title"), path: "/messages" }]);
  }, [setBreadcrumbs, t]);

  // Debounce keyword search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (keywordInput !== keyword) {
        const nextParams = new URLSearchParams(searchParams);
        if (keywordInput) {
          nextParams.set("keyword", keywordInput);
        } else {
          nextParams.delete("keyword");
        }
        nextParams.set("page", "1");
        setSearchParams(nextParams);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [keywordInput, keyword, setSearchParams, searchParams]);

  const setUnreadFilter = (value: boolean) => {
    const nextParams = new URLSearchParams(searchParams);
    if (value) {
      nextParams.set("unreadOnly", "true");
    } else {
      nextParams.delete("unreadOnly");
    }
    nextParams.set("page", "1");
    setSearchParams(nextParams);
  };

  const [selectedNotification, setSelectedNotification] =
    useState<UiNotification | null>(null);

  const {
    data: notificationsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["notifications", page, unreadOnly, keyword],
    queryFn: () =>
      getNotifications({ query: { page, pageSize, unreadOnly, keyword } }),
  });

  const markAsReadMutation = useMutation({
    mutationFn: (id: number) =>
      markNotificationAsRead({ path: { notificationId: id } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["unreadNotificationsCount"] });
    },
  });

  const handleOpenNotification = (notification: UiNotification) => {
    setSelectedNotification(notification);
    if (!notification.readAt && notification.id) {
      markAsReadMutation.mutate(notification.id);
    }
  };

  const columns: ColumnDef<UiNotification>[] = [
    {
      accessorKey: "actionDateTime",
      header: t("messagesPage.table.date"),
      cell: info => (
        <div className="flex justify-center items-center h-full text-sm">
          {format(new Date(info.getValue() as string), "yyyy-MM-dd HH:mm")}
        </div>
      ),
      size: 150,
    },
    {
      id: "status",
      header: t("messagesPage.table.status"),
      cell: ({ row }) => (
        <div className="flex justify-center items-center h-full">
          {!row.original.readAt ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {t("messagesPage.status.new")}
            </span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              {t("messagesPage.status.read")}
            </span>
          )}
        </div>
      ),
      size: 100,
    },
    {
      accessorKey: "subject",
      header: t("messagesPage.table.subject"),
      cell: info => (
        <div className="flex items-center px-4 h-full text-left overflow-hidden">
          <span
            className={`truncate ${!info.row.original.readAt ? "font-bold text-gray-900" : "text-gray-600"}`}
          >
            {info.getValue() as string}
          </span>
        </div>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex justify-center items-center h-full">
          <Button
            onClick={() => handleOpenNotification(row.original)}
            variant="secondary"
            size="md"
          >
            {t("messagesPage.buttons.view")}
          </Button>
        </div>
      ),
      size: 100,
    },
  ];

  const table = useReactTable({
    data: notificationsData?.data?.notifications ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) return <Loader />;
  if (error) return <ErrorDisplay message={error.message} />;

  return (
    <div className="space-y-8">
      <PageTitle>{t("messagesPage.title")}</PageTitle>

      <div className="flex flex-col space-y-3 pt-4">
        <div className="flex flex-col">
          <span className="block text-sm font-medium text-black mb-2">
            {t("messagesPage.table.status")}
          </span>
          <div className="flex items-center space-x-6 h-10">
            <label className="flex items-center space-x-2 cursor-pointer select-none">
              <input
                type="radio"
                name="unreadFilter"
                className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                checked={!unreadOnly}
                onChange={() => setUnreadFilter(false)}
              />
              <span className="text-sm text-gray-700">
                {t("messagesPage.filters.showAll")}
              </span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer select-none">
              <input
                type="radio"
                name="unreadFilter"
                className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                checked={unreadOnly}
                onChange={() => setUnreadFilter(true)}
              />
              <span className="text-sm text-gray-700">
                {t("messagesPage.filters.showUnread")}
              </span>
            </label>
          </div>
        </div>

        <div className="max-w-md w-full">
          <FormInput
            label={t("messagesPage.filters.search")}
            value={keywordInput}
            onChange={setKeywordInput}
            placeholder={t("messagesPage.filters.searchPlaceholder")}
          />
        </div>
      </div>

      <HeadlessTable
        table={table}
        pagination={notificationsData?.data?.pagination}
        onPageChange={p => {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.set("page", String(p));
          setSearchParams(nextParams);
        }}
      />

      {selectedNotification && (
        <Modal
          show={!!selectedNotification}
          onHide={() => setSelectedNotification(null)}
          title={t("messagesPage.modal.title")}
          size="lg"
        >
          <div className="space-y-6">
            <div className="grid grid-cols-[max-content_1fr] gap-x-8 gap-y-4">
              <div className="text-sm font-bold text-gray-700">
                {t("messagesPage.modal.timeLabel") || "Time"}:
              </div>
              <div className="text-sm text-gray-900">
                {format(
                  new Date(selectedNotification.actionDateTime!),
                  "yyyy-MM-dd HH:mm"
                )}
              </div>

              <div className="text-sm font-bold text-gray-700">
                {t("messagesPage.modal.subjectLabel") || "Subject"}:
              </div>
              <div className="text-sm text-gray-900 font-medium">
                {selectedNotification.subject}
              </div>

              <div className="text-sm font-bold text-gray-700">
                {t("messagesPage.modal.contentLabel") || "Message content"}:
              </div>
              <Linkify
                as="div"
                className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed"
                options={{
                  target: "_blank",
                  rel: "noopener noreferrer",
                  className: "text-blue-600 underline",
                }}
              >
                {selectedNotification.content}
              </Linkify>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                onClick={() => setSelectedNotification(null)}
                variant="primary"
              >
                {t("common.close")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
